/**
 * File-preview store — Zustand.
 *
 * Drives a single global <FilePreviewModal /> (mounted in main.tsx) so any
 * authed file (invoice source document, approver supporting document) opens as
 * an in-page popup over the current tab instead of a new browser tab.
 *
 * Consumers push a request via openFilePreview({ path, ... }); the modal fetches
 * the blob from `path` (auth'd proxy stream) and renders it. Opening outside a
 * React component is supported via useFilePreviewStore.getState().openFilePreview().
 */

import { create } from 'zustand';

export interface FilePreviewRequest {
  /** API path (relative to apiClient baseURL) that streams the file bytes. */
  path: string;
  /** Heading shown on the modal; also the download filename if downloadName is absent. */
  title?: string;
  /** Suggested filename when the user clicks Download. */
  downloadName?: string;
  /** Optional MIME hint used until the blob's own Content-Type is known. */
  mimeHint?: string;
}

export interface FilePreviewState {
  open: boolean;
  path: string | null;
  title: string;
  downloadName?: string;
  mimeHint?: string;
  openFilePreview: (req: FilePreviewRequest) => void;
  close: () => void;
}

export const useFilePreviewStore = create<FilePreviewState>((set) => ({
  open: false,
  path: null,
  title: 'File preview',
  downloadName: undefined,
  mimeHint: undefined,
  openFilePreview: ({ path, title, downloadName, mimeHint }) =>
    set({
      open: true,
      path,
      title: title ?? 'File preview',
      downloadName,
      mimeHint,
    }),
  close: () => set({ open: false, path: null }),
}));
